import { Link } from 'react-router-dom';
import { theme } from '../theme.js';
import { Logo } from '../components/Logo.js';

const CONTACT = 'enquiries@mustertap.com';
const UPDATED = '3 November 2026';

/** Public privacy policy at /privacy, reachable signed in or out. */
export function PrivacyPage() {
  return (
    <div style={{ minHeight: '100dvh', background: theme.bg, color: theme.text, fontFamily: theme.font }}>
      <style>{CSS}</style>
      <header className="pv-wrap pv-head">
        <Link to="/" aria-label="MusterTap home" style={{ color: 'inherit', textDecoration: 'none' }}>
          <Logo size={24} />
        </Link>
      </header>

      <main className="pv-wrap pv-body">
        <h1>Privacy policy</h1>
        <p className="pv-muted">Last updated {UPDATED}</p>

        <p>
          MusterTap is an app for volunteer emergency-service units to run rosters, shift cover, leave and
          vehicle and equipment checks. It is operated by KinCue (ABN 75 863 951 924). This page explains what personal information MusterTap holds, why,
          who can see it and what you can do about it. We handle personal information in line with the
          Australian Privacy Principles.
        </p>

        <h2>What we hold</h2>
        <ul>
          <li><b>Your account:</b> your name and email address, and your sign-in details (managed by Amazon Cognito; we never see your password).</li>
          <li><b>Your place in the unit:</b> your role, team, operational status, level, qualifications (with expiry dates), positions and roster assignments, as entered by you or your unit’s admins.</li>
          <li><b>Availability and leave:</b> shifts you put your hand up for, cover and swap requests, and leave dates, with a reason if one is given.</li>
          <li><b>Checks:</b> vehicle and equipment checks you complete, including notes and photos you take.</li>
          <li><b>Notifications:</b> if you turn on notifications, a push address for each of your devices, and your choice about email.</li>
          <li><b>Activity:</b> a log of changes made in your unit (who changed what, and when), and when you last used the app.</li>
        </ul>
        <p>We don’t use advertising or analytics trackers, and we don’t sell or share your information for marketing.</p>
        <p>The demo video on our front page is hosted by YouTube. Nothing loads from YouTube until you press play. If you do, YouTube’s own privacy policy applies to that video.</p>

        <h2>Why we hold it</h2>
        <p>
          Only to run the service for your unit: showing rosters and crews, finding people who are free and
          qualified to cover a shift, telling you about things that need you, and keeping a record of checks
          and changes.
        </p>

        <h2>Who can see it</h2>
        <ul>
          <li><b>Your unit:</b> members see names, teams and rosters. Unit admins see and manage member details. Team leaders see their team’s leave; leave reasons are shown only to admins and your own team leaders.</li>
          <li><b>Other units:</b> nothing, unless you are also a member there.</li>
          <li><b>MusterTap’s operators:</b> only as needed to run, support and fix the service.</li>
          <li><b>Service providers:</b> the app runs on Amazon Web Services in Sydney, Australia (hosting, database, sign-in and email). Push notifications pass through your device maker’s push service (Apple, Google or Microsoft).</li>
        </ul>

        <h2>Email</h2>
        <p>
          We send email only about your use of MusterTap: invites, sign-in codes and password resets, and
          notifications when none of your devices has push notifications turned on. You can stop notification
          emails at any time under <b>Profile → Notifications</b>. If an email to your address bounces or is
          marked as spam, we stop emailing it and your unit admin sees that the address needs fixing.
        </p>

        <h2>How long we keep it</h2>
        <p>
          We keep your information while your unit uses MusterTap. Delete your account under <b>Profile</b> and
          we remove your email, sign-in, qualifications, positions and devices; your name stays on past records
          (such as completed checks and rosters) so your unit’s history still makes sense. A unit admin can also
          remove you from a unit.
        </p>

        <h2>Access, correction and complaints</h2>
        <p>
          You can see and change most of your details in the app, or ask your unit admin. For anything else,
          including a copy of what we hold about you or a privacy complaint, email{' '}
          <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. We’ll reply within 30 days. If you’re not satisfied, you can
          contact the Office of the Australian Information Commissioner (oaic.gov.au).
        </p>

        <h2>Changes</h2>
        <p>If we change this policy we’ll update this page and the date above.</p>

        <h2>Who we are</h2>
        <p>
          MusterTap is operated by KinCue, ABN 75 863 951 924, Australia. Contact:{' '}
          <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
        </p>

        <p className="pv-back"><Link to="/">← Back to MusterTap</Link></p>
      </main>
    </div>
  );
}

const CSS = `
.pv-wrap { max-width: 720px; margin: 0 auto; padding-inline: 20px; }
.pv-head { padding-top: 20px; padding-bottom: 8px; }
.pv-body { padding-bottom: max(40px, env(safe-area-inset-bottom, 0px)); font-size: 15px; line-height: 1.6; }
.pv-body h1 { font-size: 28px; margin: 16px 0 4px; }
.pv-body h2 { font-size: 18px; margin: 28px 0 6px; }
.pv-body ul { padding-left: 20px; margin: 8px 0; }
.pv-body li { margin: 6px 0; }
.pv-body a { color: ${theme.accent}; }
.pv-muted { color: ${theme.textMuted}; font-size: 13px; margin-top: 0; }
.pv-back { margin-top: 32px; }
`;
